import type { ObjektovyTyp, ZaznamObjektu, ZapisObjektuType } from 'jvf-parser';
import { labelForAttribute } from 'jvf-parser';
import { getActiveVersion } from '../state/activeVersion.js';
import type OlMap from 'ol/Map.js';
import type { JvfVectorLayer } from '../map/jvfLayers.js';
import { findFeature } from '../map/jvfLayers.js';
import { highlightFeature, clearHighlight, zoomToFeature } from '../map/highlight.js';
import { highlightThreeFeature, clearThreeHighlight, zoomToThreeFeature, applyFeatureFilter } from '../viewer3d/threeScene.js';
import { getIs3dActive, notifyMapAreaResized } from './toggle3d.js';
import { resolveZaznamId } from '../state/zaznamId.js';
import {
  LEVEL_NONE,
  isLevelVisible,
  isProjectVisible,
  levelKeyLabel,
  matchesFeatureFilter,
  resolveLevelKey,
  setLevelVisible,
  setProjectVisible,
  subscribeFeatureFilter,
  type LevelKey,
} from '../state/featureFilter.js';
import {
  getProject,
  getProjects,
  isMultiProject,
  resolveProjectKey,
} from '../state/projects.js';

type ContentFilter = 'all' | 'ZPS' | 'TI' | 'DI' | 'GAD' | 'OPL';

// ── DOM refs ──────────────────────────────────────────────────────────────────
const panel        = document.getElementById('features-panel')         as HTMLElement;
const resizeHandle = document.getElementById('features-panel-resize')  as HTMLElement;
const list         = document.getElementById('features-list')          as HTMLDivElement;
const summaryEl    = document.getElementById('features-summary')       as HTMLDivElement;
const searchInput  = document.getElementById('features-search')        as HTMLInputElement;
const levelFilterEl = document.getElementById('features-level-filter') as HTMLDivElement;
const projectFilterEl = document.getElementById('features-project-filter') as HTMLDivElement;
const filterBtns   = panel.querySelectorAll<HTMLButtonElement>('.features-filter-btn');

// ── State ─────────────────────────────────────────────────────────────────────
let currentObjekty: ObjektovyTyp[] = [];
let currentFilter: ContentFilter = 'all';
let currentSearch = '';
let activeRow: HTMLElement | null = null;
let activeTarget: { elementName: string; objectId: string } | null = null;
let getJvfLayers: (() => JvfVectorLayer[]) | null = null;
let olMap: OlMap | null = null;
let onHideCallback: (() => void) | null = null;
const expandedGroups = new Set<string>();
const expandedRecords = new Set<string>();

// ── Utils ─────────────────────────────────────────────────────────────────────
function recordKey(elementName: string, objectId: string): string {
  return `${elementName}::${objectId}`;
}

/**
 * Klíč skupiny v seznamu — elementName kvalifikovaný projektem, aby při
 * více projektech šly skupiny stejného typu rozbalovat nezávisle.
 * Při jednom projektu je klíč čistý elementName (beze změny chování).
 */
function groupKey(ot: ObjektovyTyp): string {
  const projectId = isMultiProject() ? resolveProjectKey(ot) : null;
  return projectId ? `${projectId}:${ot.elementName}` : ot.elementName;
}

function zapisLabel(z: ZapisObjektuType): { label: string; cls: string } {
  switch (z) {
    case 'i': return { label: 'I', cls: 'zapis-i' };
    case 'u': return { label: 'U', cls: 'zapis-u' };
    case 'd': return { label: 'D', cls: 'zapis-d' };
    default:  return { label: 'R', cls: 'zapis-r' };
  }
}

function matchesFilter(ot: ObjektovyTyp): boolean {
  if (currentFilter === 'all') return true;
  return ot.obsahovaCast === currentFilter;
}

function matchesSearch(ot: ObjektovyTyp, z: ZaznamObjektu): boolean {
  if (!currentSearch) return true;
  const q = currentSearch.toLowerCase();
  if (ot.nazev.toLowerCase().includes(q)) return true;
  if (ot.elementName.toLowerCase().includes(q)) return true;
  const id = z.commonAttributes?.id ?? '';
  if (id.toLowerCase().includes(q)) return true;
  return false;
}

// ── Filtr úrovní umístění (LEVEL) ─────────────────────────────────────────────

/**
 * Zjistí úrovně umístění přítomné v datech, seřazené vzestupně; skupina
 * „bez úrovně" (`LEVEL_NONE`) je vždy poslední.
 */
function collectPresentLevelKeys(objekty: ObjektovyTyp[]): LevelKey[] {
  const present = new Set<LevelKey>();
  for (const ot of objekty) {
    for (const z of ot.zaznamy) present.add(resolveLevelKey(z));
  }
  const numeric = [...present]
    .filter((k) => k !== LEVEL_NONE)
    .sort((a, b) => Number(a) - Number(b));
  if (present.has(LEVEL_NONE)) numeric.push(LEVEL_NONE);
  return numeric;
}

/**
 * Vykreslí chips filtru úrovní umístění podle aktuálních dat. Když je
 * v datech jen jedna skupina úrovní (nebo žádná), filtr nedává smysl —
 * řádek se skryje. Aktivní chip = úroveň viditelná; klik přepíná.
 * Filtr se promítá do tabulky, 2D mapy i 3D scény (viz subscribe
 * v `initFeaturesPanel`).
 */
function renderLevelChips(): void {
  const keys = collectPresentLevelKeys(currentObjekty);
  if (keys.length < 2) {
    levelFilterEl.style.display = 'none';
    levelFilterEl.innerHTML = '';
    return;
  }
  levelFilterEl.style.display = '';
  levelFilterEl.innerHTML = '';

  const label = document.createElement('span');
  label.className = 'features-level-label';
  label.textContent = 'Úroveň:';
  levelFilterEl.appendChild(label);

  for (const key of keys) {
    const chip = document.createElement('button');
    const visible = isLevelVisible(key);
    chip.className = `level-chip${visible ? ' active' : ''}`;
    chip.textContent = levelKeyLabel(key);
    chip.title = key === LEVEL_NONE
      ? 'Záznamy bez atributu úrovně umístění'
      : `Úroveň umístění ${levelKeyLabel(key)} (UrovenUmisteniObjektuZPS/TI/DI)`;
    chip.addEventListener('click', () => {
      setLevelVisible(key, !isLevelVisible(key));
    });
    levelFilterEl.appendChild(chip);
  }
}

// ── Filtr projektů ────────────────────────────────────────────────────────────

/**
 * Vykreslí chips filtru projektů — zobrazí se jen při ≥2 načtených
 * projektech (stejný vzor jako level chips). Aktivní chip = projekt
 * viditelný; klik přepíná. Filtr se promítá do tabulky, 2D mapy i 3D
 * scény přes `state/featureFilter.ts` (dimenze projekt).
 */
function renderProjectChips(): void {
  const projects = getProjects();
  if (!isMultiProject()) {
    projectFilterEl.style.display = 'none';
    projectFilterEl.innerHTML = '';
    return;
  }
  projectFilterEl.style.display = '';
  projectFilterEl.innerHTML = '';

  const label = document.createElement('span');
  label.className = 'features-level-label';
  label.textContent = 'Projekt:';
  projectFilterEl.appendChild(label);

  for (const project of projects) {
    const chip = document.createElement('button');
    const visible = isProjectVisible(project.id);
    chip.className = `level-chip project-chip${visible ? ' active' : ''}`;
    chip.title = `Zobrazit / skrýt prvky projektu „${project.nazev}"`;

    const dot = document.createElement('span');
    dot.className = 'project-dot';
    dot.style.background = project.color;

    const nameEl = document.createElement('span');
    nameEl.className = 'project-chip-name';
    nameEl.textContent = project.nazev;

    chip.append(dot, nameEl);
    chip.addEventListener('click', () => {
      setProjectVisible(project.id, !isProjectVisible(project.id));
    });
    projectFilterEl.appendChild(chip);
  }
}

// ── Summary ───────────────────────────────────────────────────────────────────
function renderSummary(): void {
  const totalTypes = currentObjekty.length;
  const totalRecords = currentObjekty.reduce((sum, ot) => sum + ot.zaznamy.length, 0);
  if (totalTypes === 0) {
    summaryEl.textContent = 'Žádná data.';
    summaryEl.className = 'features-summary summary-empty';
    return;
  }
  summaryEl.textContent = `${totalTypes} ${totalTypes === 1 ? 'typ' : totalTypes < 5 ? 'typy' : 'typů'}, ${totalRecords} ${totalRecords === 1 ? 'záznam' : totalRecords < 5 ? 'záznamy' : 'záznamů'}`;
  summaryEl.className = 'features-summary summary-ok';
}

// ── Group toggle ──────────────────────────────────────────────────────────────
function toggleGroup(key: string): void {
  if (expandedGroups.has(key)) {
    expandedGroups.delete(key);
  } else {
    expandedGroups.add(key);
  }
  renderRows();
}

// ── Row click ─────────────────────────────────────────────────────────────────
function handleRowClick(row: HTMLElement, elementName: string, objectId: string): void {
  // Druhý klik → zrušit (toggle off)
  if (activeRow === row) {
    row.classList.remove('active');
    activeRow = null;
    activeTarget = null;
    clearHighlight();
    clearThreeHighlight();
    // Sbalit detail
    expandedRecords.delete(recordKey(elementName, objectId));
    const detailEl = row.nextElementSibling;
    if (detailEl?.classList.contains('feature-detail')) {
      (detailEl as HTMLElement).style.display = 'none';
    }
    return;
  }

  if (activeRow) activeRow.classList.remove('active');
  activeRow = row;
  activeTarget = { elementName, objectId };
  row.classList.add('active');

  // Rozbalit detail tohoto záznamu
  expandedRecords.add(recordKey(elementName, objectId));
  const detailEl = row.nextElementSibling;
  if (detailEl?.classList.contains('feature-detail')) {
    (detailEl as HTMLElement).style.display = '';
  }

  applyHighlight(elementName, objectId);
}

function applyHighlight(elementName: string, objectId: string): void {
  if (!olMap || !getJvfLayers) return;

  if (getIs3dActive()) {
    highlightThreeFeature(elementName, objectId);
    zoomToThreeFeature(elementName, objectId);
    return;
  }

  const feature = findFeature(getJvfLayers(), elementName, objectId);
  if (!feature) return;

  highlightFeature(feature);
  zoomToFeature(olMap, feature);
}

/**
 * Re-apply highlight + zoom for the currently selected feature in the active view.
 * Volá se po přepnutí 2D ↔ 3D, aby výběr přežil změnu pohledu.
 */
export function reapplyActiveFeatureHighlight(): void {
  if (!activeTarget) return;
  applyHighlight(activeTarget.elementName, activeTarget.objectId);
}

/**
 * Vytáhne Z-souřadnice (nadmořské výšky) ze všech geometrií záznamu. Z je
 * 3. složka souřadnic (index i+2) u geometrií s `srsDimension >= 3`. Plochy
 * mají dvojí geometrii (2D Polygon + 3D MultiCurve) — Polygon je obvykle 2D,
 * takže výšky pocházejí hlavně z MultiCurve/linií/bodů. Vrací jen konečné
 * hodnoty (bez NaN/Infinity).
 */
function extractElevations(z: ZaznamObjektu): number[] {
  const out: number[] = [];
  const fromFlat = (coords: number[], srsDim: number): void => {
    const dim = srsDim > 0 ? srsDim : 2;
    if (dim < 3) return;
    for (let i = 0; i < coords.length; i += dim) {
      const zv = coords[i + 2];
      if (zv !== undefined && Number.isFinite(zv)) out.push(zv);
    }
  };
  for (const geom of z.geometrie ?? []) {
    switch (geom.type) {
      case 'Point': {
        const zv = geom.data.coordinates[2];
        if (zv !== undefined && Number.isFinite(zv)) out.push(zv);
        break;
      }
      case 'LineString':
        fromFlat(geom.data.coordinates, geom.data.srsDimension);
        break;
      case 'Polygon':
        fromFlat(geom.data.exterior, geom.data.srsDimension);
        break;
      case 'MultiCurve':
        for (const c of geom.data.curves) fromFlat(c.coordinates, c.srsDimension);
        break;
    }
  }
  return out;
}

/** Formát výšky na 2 desetinná místa, česká desetinná čárka. */
function fmtElevation(v: number): string {
  return v.toFixed(2).replace('.', ',');
}

// ── Render ────────────────────────────────────────────────────────────────────
function renderAttributes(z: ZaznamObjektu): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'feature-detail-inner';

  // CommonAttributes
  const common = z.commonAttributes ?? {};
  const commonEntries = Object.entries(common).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (commonEntries.length > 0) {
    const h = document.createElement('div');
    h.className = 'feature-detail-section-title';
    h.textContent = 'Společné atributy';
    wrap.appendChild(h);
    wrap.appendChild(buildKVTable(commonEntries));
  }

  // Specific attributes
  const attrs = z.attributes ?? {};
  const attrEntries = Object.entries(attrs).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (attrEntries.length > 0) {
    const h = document.createElement('div');
    h.className = 'feature-detail-section-title';
    h.textContent = 'Atributy objektu';
    wrap.appendChild(h);
    wrap.appendChild(buildKVTable(attrEntries, true));
  }

  // Geometrie summary
  const geomCount = z.geometrie?.length ?? 0;
  const geomLine = document.createElement('div');
  geomLine.className = 'feature-detail-meta';
  if (geomCount === 0) {
    geomLine.textContent = 'Žádná geometrie';
  } else {
    const types = z.geometrie.map((g) => g.type).join(', ');
    geomLine.textContent = `${geomCount} ${geomCount === 1 ? 'geometrie' : 'geometrie'} (${types})`;
  }
  wrap.appendChild(geomLine);

  // Nadmořská výška (Z) — z geometrie záznamu. Bod má jednu hodnotu, linie/plochy
  // rozsah. Zobrazí se jen pokud má geometrie Z-složku (3D).
  const elevations = extractElevations(z);
  if (elevations.length > 0) {
    const min = Math.min(...elevations);
    const max = Math.max(...elevations);
    const elevLine = document.createElement('div');
    elevLine.className = 'feature-detail-meta';
    elevLine.textContent =
      min === max
        ? `Nadmořská výška: ${fmtElevation(min)} m`
        : `Nadmořská výška: ${fmtElevation(min)}–${fmtElevation(max)} m`;
    wrap.appendChild(elevLine);
  }

  if (commonEntries.length === 0 && attrEntries.length === 0 && geomCount === 0) {
    const empty = document.createElement('div');
    empty.className = 'feature-detail-empty';
    empty.textContent = 'Žádné atributy.';
    wrap.appendChild(empty);
  }

  return wrap;
}

/**
 * @param translate Pokud `true`, u číselníkových atributů doplní za kód
 *   český popisek z číselníku DTM (`kód — text`). Zapíná se jen pro sekci
 *   „Atributy objektu" (A5), ne pro „Společné atributy" (identifikátory/data).
 */
function buildKVTable(entries: [string, unknown][], translate = false): HTMLElement {
  const tbl = document.createElement('table');
  tbl.className = 'feature-detail-table';
  const tbody = document.createElement('tbody');
  const version = getActiveVersion();
  for (const [k, v] of entries) {
    const tr = document.createElement('tr');
    const tdK = document.createElement('td');
    tdK.className = 'kv-key';
    tdK.textContent = k;
    const tdV = document.createElement('td');
    tdV.className = 'kv-value';
    const label = translate && v !== null ? labelForAttribute(k, v, version) : undefined;
    tdV.textContent = label !== undefined ? `${String(v)} — ${label}` : String(v);
    tr.append(tdK, tdV);
    tbody.appendChild(tr);
  }
  tbl.appendChild(tbody);
  return tbl;
}

function renderRows(): void {
  list.innerHTML = '';
  // Ponecháme activeRow null jen pokud cíl už není v listu
  activeRow = null;

  // Index záznamu držíme z původního pole `ot.zaznamy` (před search filtrem),
  // aby syntetické klíče (`resolveZaznamId`) seděly s 2D vrstvami a 3D scénou.
  const filtered = currentObjekty
    .filter(matchesFilter)
    .map((ot) => ({
      ot,
      zaznamy: ot.zaznamy
        .map((z, idx) => ({ z, idx }))
        // Odfiltrované záznamy (filtr úrovní) se skrývají úplně — stejné
        // chování jako filtr obsahové části a fulltext, a konzistentní
        // s mapou, kde odfiltrovaný prvek také zmizí.
        .filter(({ z }) => matchesSearch(ot, z) && matchesFeatureFilter(z)),
    }))
    .filter(({ zaznamy }) => zaznamy.length > 0);

  if (filtered.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'features-list-empty';
    empty.textContent = currentObjekty.length === 0
      ? 'Žádná data. Nahrajte JVF soubor.'
      : 'Žádné položky pro vybraný filtr.';
    list.appendChild(empty);
    return;
  }

  for (const { ot, zaznamy } of filtered) {
    const gKey = groupKey(ot);
    const expanded = expandedGroups.has(gKey);
    const project = isMultiProject()
      ? getProject(resolveProjectKey(ot) ?? '')
      : undefined;

    // Group header
    const header = document.createElement('div');
    header.className = `feature-group-header part-${ot.obsahovaCast}`;
    header.dataset['element'] = ot.elementName;
    header.title = project
      ? `${ot.nazev} — ${ot.obsahovaCast} · projekt ${project.nazev} (klikni pro rozbalení)`
      : `${ot.nazev} — ${ot.obsahovaCast} (klikni pro rozbalení)`;

    const chevron = document.createElement('span');
    chevron.className = `group-chevron${expanded ? ' expanded' : ''}`;
    chevron.textContent = expanded ? '▾' : '▸';

    const partBadge = document.createElement('span');
    partBadge.className = `part-badge part-${ot.obsahovaCast}`;
    partBadge.textContent = String(ot.obsahovaCast);

    const nameSpan = document.createElement('span');
    nameSpan.className = 'feature-group-name';
    nameSpan.textContent = ot.nazev;

    const elSpan = document.createElement('span');
    elSpan.className = 'feature-group-element';
    elSpan.textContent = ot.elementName;

    const countBadge = document.createElement('span');
    countBadge.className = 'group-count-badge group-count-features';
    countBadge.textContent = String(zaznamy.length);

    header.append(chevron, partBadge);
    // Barevná tečka projektu — jen při více projektech (odlišení stejných
    // objektových typů z různých souborů).
    if (project) {
      const projectDot = document.createElement('span');
      projectDot.className = 'project-dot';
      projectDot.style.background = project.color;
      projectDot.title = `Projekt: ${project.nazev}`;
      header.appendChild(projectDot);
    }
    header.append(nameSpan, elSpan, countBadge);
    header.addEventListener('click', () => toggleGroup(gKey));
    list.appendChild(header);

    if (!expanded) continue;

    // Detail rows
    for (const { z, idx } of zaznamy) {
      // Identifikace záznamu: DTM ID, nebo syntetický klíč pro záznamy bez
      // ID (nové prvky `ZapisObjektu='i'`) — i ty jsou plnohodnotně
      // klikatelné (zoom, highlight, detail atributů).
      const objectId = resolveZaznamId(ot.elementName, z, idx);
      const hasDtmId = !!z.commonAttributes?.id;
      const hasTarget = (z.geometrie?.length ?? 0) > 0;
      const key = recordKey(ot.elementName, objectId);
      const isActive = activeTarget !== null
        && activeTarget.elementName === ot.elementName
        && activeTarget.objectId === objectId;

      const row = document.createElement('div');
      row.className = `feature-row${hasTarget ? ' has-target' : ' no-target'}${isActive ? ' active' : ''}`;
      row.dataset['element'] = ot.elementName;
      row.dataset['id'] = objectId;

      const zoomIcon = document.createElement('span');
      zoomIcon.className = 'feature-zoom-icon';
      zoomIcon.textContent = '⌖';
      zoomIcon.title = hasTarget ? 'Zoom na objekt' : 'Objekt nelze lokalizovat (chybí geometrie)';

      const zapis = zapisLabel(z.zapisObjektu);
      const zapisBadge = document.createElement('span');
      zapisBadge.className = `zapis-badge ${zapis.cls}`;
      zapisBadge.textContent = zapis.label;
      zapisBadge.title = `ZapisObjektu='${z.zapisObjektu}'`;

      const idSpan = document.createElement('span');
      idSpan.className = 'feature-row-id';
      // Zobrazujeme surové DTM ID (bez případného projektového prefixu
      // v `objectId`) — projekt identifikuje tečka v hlavičce skupiny.
      idSpan.textContent = hasDtmId ? z.commonAttributes!.id! : `bez ID (#${idx + 1})`;

      const popisSpan = document.createElement('span');
      popisSpan.className = 'feature-row-popis';
      popisSpan.textContent = z.commonAttributes?.popisObjektu ?? '';

      row.append(zoomIcon, zapisBadge, idSpan, popisSpan);
      if (hasTarget) {
        row.addEventListener('click', () => handleRowClick(row, ot.elementName, objectId));
      }
      list.appendChild(row);

      if (isActive) activeRow = row;

      // Detail (atributy) — vytvoříme vždy, ale display řídíme expandedRecords
      const detail = document.createElement('div');
      detail.className = 'feature-detail';
      detail.style.display = expandedRecords.has(key) ? '' : 'none';
      detail.appendChild(renderAttributes(z));
      list.appendChild(detail);
    }
  }
}

// ── Filter / search ───────────────────────────────────────────────────────────
function setFilter(filter: ContentFilter): void {
  currentFilter = filter;
  filterBtns.forEach((btn) => {
    btn.classList.toggle('active', btn.dataset['filter'] === filter);
  });
  renderRows();
}

// ── Panel open/close ──────────────────────────────────────────────────────────
export function showFeatures(objekty: ObjektovyTyp[]): void {
  currentObjekty = objekty;
  if (!expandedGroups.size && objekty.length > 0 && objekty.length <= 8) {
    // Auto-expand u malých souborů
    for (const ot of objekty) expandedGroups.add(groupKey(ot));
  }
  renderSummary();
  renderLevelChips();
  renderProjectChips();
  renderRows();
  panel.style.display = '';
  notifyMapAreaResized();
}

export function hideFeatures(): void {
  panel.style.display = 'none';
  clearHighlight();
  clearThreeHighlight();
  if (activeRow) { activeRow.classList.remove('active'); activeRow = null; }
  activeTarget = null;
  onHideCallback?.();
  notifyMapAreaResized();
}

export function isFeaturesPanelVisible(): boolean {
  return panel.style.display !== 'none';
}

/**
 * Vyber prvek z mapy v panelu — rozbalí příslušnou grupu, najde řádek,
 * zobrazí detail, scrollne do view a označí jako aktivní. Volá se z map
 * click handlerů (2D / 3D), aby uživatel viděl atributy klikutého objektu.
 *
 * Nemění highlight v mapě — předpokládá se, že volající (map handler) si
 * highlight řeší sám. Pokud panel není viditelný, no-op.
 */
export function selectFeatureInPanel(elementName: string, objectId: string): void {
  if (!isFeaturesPanelVisible()) return;
  if (!objectId) return;

  const key = recordKey(elementName, objectId);
  expandedRecords.add(key);

  // Najít objektový typ obsahující záznam — při více projektech může
  // existovat víc typů se stejným elementName; rozhoduje shoda objectId
  // (ten je kvalifikovaný projektem).
  let found: { ot: ObjektovyTyp; z: ZaznamObjektu } | null = null;
  for (const ot of currentObjekty) {
    if (ot.elementName !== elementName) continue;
    const z = ot.zaznamy.find(
      (zz, idx) => resolveZaznamId(elementName, zz, idx) === objectId,
    );
    if (z) {
      found = { ot, z };
      break;
    }
  }

  // Zajistit, že daná grupa je rozbalená
  expandedGroups.add(found ? groupKey(found.ot) : elementName);

  // Pokud filtr / search skryje typ, zruš filtr (uživatel klikl, chce vidět)
  if (found) {
    const { ot, z } = found;
    if (currentFilter !== 'all' && ot.obsahovaCast !== currentFilter) {
      setFilter('all');
    }
    if (currentSearch) {
      // Zkontroluj, jestli se objekt projde aktuálním filtrem; pokud ne, vyčisti
      if (!matchesSearch(ot, z)) {
        currentSearch = '';
        searchInput.value = '';
      }
    }
    // Pokud je záznam skrytý filtrem prvků (úroveň / projekt), zviditelni
    // jeho hodnoty — uživatel na prvek klikl, chce ho vidět (stejná logika
    // jako u search).
    if (!matchesFeatureFilter(z)) {
      setLevelVisible(resolveLevelKey(z), true);
      const projectKey = resolveProjectKey(z);
      if (projectKey && !isProjectVisible(projectKey)) {
        setProjectVisible(projectKey, true);
      }
    }
  }

  // Aktualizovat target před renderem, aby renderRows nastavil active class
  activeTarget = { elementName, objectId };
  renderRows();

  // Najít row v DOM a scrollnout
  const row = list.querySelector<HTMLElement>(
    `.feature-row[data-element="${CSS.escape(elementName)}"][data-id="${CSS.escape(objectId)}"]`,
  );
  if (row) {
    activeRow = row;
    row.classList.add('active');
    // Detail rozbalit
    const detailEl = row.nextElementSibling;
    if (detailEl?.classList.contains('feature-detail')) {
      (detailEl as HTMLElement).style.display = '';
    }
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

// ── Init ──────────────────────────────────────────────────────────────────────
export function initFeaturesPanel(
  map: OlMap,
  getLayers: () => JvfVectorLayer[],
  opts: { onHide?: () => void } = {},
): void {
  olMap = map;
  getJvfLayers = getLayers;
  onHideCallback = opts.onHide ?? null;

  // Escape — zavřít
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isFeaturesPanelVisible()) hideFeatures();
  });

  // Filter buttons
  filterBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      setFilter((btn.dataset['filter'] as ContentFilter) ?? 'all');
    });
  });

  // Search
  searchInput.addEventListener('input', () => {
    currentSearch = searchInput.value.trim();
    renderRows();
  });

  // Filtr prvků (úrovně umístění) — při každé změně promítnout do:
  //   1. chips UI (sync active tříd),
  //   2. tabulky Přehledu prvků,
  //   3. 2D mapy (`layer.changed()` → style fn se přepočítá, vzor
  //      changesetToggle),
  //   4. 3D scény (přepočet visibility per objekt).
  subscribeFeatureFilter(() => {
    renderLevelChips();
    renderProjectChips();
    renderRows();
    if (getJvfLayers) {
      for (const { olLayer } of getJvfLayers()) olLayer.changed();
    }
    applyFeatureFilter();
  });

  // Klik na row detail vnitřek by neměl zavírat
  list.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('.feature-detail')) {
      e.stopPropagation();
    }
  });

  // Resize handle — drag levou hranu
  resizeHandle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panel.offsetWidth;

    function onMove(ev: MouseEvent): void {
      const delta = startX - ev.clientX;
      const newWidth = Math.max(280, Math.min(window.innerWidth * 0.6, startWidth + delta));
      panel.style.width = `${newWidth}px`;
    }
    function onUp(): void {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  });
}
